import Dexie, { type Table } from 'dexie';
import { type SetPatch } from '../domain/workout';

/**
 * Per-set update enqueued for retry. Held only when the live mutation failed
 * (typically network drop). Each row is one independent operation; the worker
 * applies them oldest-first.
 *
 * Notes on idempotency: server-side updateSet is idempotent per setId × patch
 * (last write wins on each scalar field), so duplicate flushes are safe.
 */
export interface PendingSetUpdate {
  id?: number;
  setId: string;
  patch: SetPatch;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

/**
 * Session-level op (finish or cancel) queued when the round-trip failed
 * mid-workout. Both operations are idempotent — finish stamps completedAt
 * (last write wins, we capture the click moment client-side); cancel
 * deletes a row that may already be gone (treated as success).
 */
export interface PendingSessionOp {
  id?: number;
  sessionId: string;
  kind: 'finish' | 'cancel';
  /** ISO timestamp captured at the moment the user tapped Finish — the
   *  server uses this when the queued op finally lands so the completed_at
   *  reflects when the lifter actually finished, not when the network
   *  recovered. Unused for cancel. */
  completedAt: string | null;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

const DB_NAME = 'trenora-offline-v1';
const MAX_ATTEMPTS = 8;

class TrenoraOfflineDB extends Dexie {
  pendingSetUpdates!: Table<PendingSetUpdate, number>;
  pendingSessionOps!: Table<PendingSessionOp, number>;

  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      // ++id  — auto-incremented PK; setId is indexed for cache lookups.
      pendingSetUpdates: '++id, setId, createdAt',
    });
    // v2 adds session-level ops (finish/cancel) — kept in the same DB so
    // a single sync-worker can flush both queues in one pass.
    this.version(2).stores({
      pendingSetUpdates: '++id, setId, createdAt',
      pendingSessionOps: '++id, sessionId, kind, createdAt',
    });
  }
}

export const db = new TrenoraOfflineDB();

export async function enqueueSetUpdate(
  setId: string,
  patch: SetPatch,
  error: unknown,
): Promise<void> {
  await db.pendingSetUpdates.add({
    setId,
    patch,
    createdAt: Date.now(),
    attempts: 0,
    lastError: error instanceof Error ? error.message : null,
  });
}

export async function listPending(): Promise<PendingSetUpdate[]> {
  return db.pendingSetUpdates.orderBy('createdAt').toArray();
}

export async function countPending(): Promise<number> {
  return db.pendingSetUpdates.count();
}

export async function clearPending(): Promise<void> {
  await db.pendingSetUpdates.clear();
  await db.pendingSessionOps.clear();
}

export async function bumpAttempt(id: number, error: unknown): Promise<boolean> {
  const row = await db.pendingSetUpdates.get(id);
  if (!row) return false;
  const next = row.attempts + 1;
  if (next >= MAX_ATTEMPTS) {
    await db.pendingSetUpdates.delete(id);
    return false; // gave up
  }
  await db.pendingSetUpdates.update(id, {
    attempts: next,
    lastError: error instanceof Error ? error.message : null,
  });
  return true;
}

export async function deletePending(id: number): Promise<void> {
  await db.pendingSetUpdates.delete(id);
}

// ------ Session ops ------

export async function enqueueSessionOp(
  sessionId: string,
  kind: 'finish' | 'cancel',
  completedAt: string | null,
  error: unknown,
): Promise<void> {
  // Replace any older queued op for the same session — last user intent wins.
  // (cancel after finish should win; finish after cancel rarely makes sense
  // but doesn't hurt.) Keeps the queue tidy and avoids replaying superseded
  // requests against the server.
  await db.transaction('rw', db.pendingSessionOps, async () => {
    const existing = await db.pendingSessionOps.where('sessionId').equals(sessionId).toArray();
    for (const row of existing) {
      if (row.id !== undefined) await db.pendingSessionOps.delete(row.id);
    }
    await db.pendingSessionOps.add({
      sessionId,
      kind,
      completedAt,
      createdAt: Date.now(),
      attempts: 0,
      lastError: error instanceof Error ? error.message : null,
    });
  });
}

export async function listPendingSessionOps(): Promise<PendingSessionOp[]> {
  return db.pendingSessionOps.orderBy('createdAt').toArray();
}

export async function countPendingSessionOps(): Promise<number> {
  return db.pendingSessionOps.count();
}

export async function bumpSessionOpAttempt(id: number, error: unknown): Promise<boolean> {
  const row = await db.pendingSessionOps.get(id);
  if (!row) return false;
  const next = row.attempts + 1;
  if (next >= MAX_ATTEMPTS) {
    await db.pendingSessionOps.delete(id);
    return false;
  }
  await db.pendingSessionOps.update(id, {
    attempts: next,
    lastError: error instanceof Error ? error.message : null,
  });
  return true;
}

export async function deletePendingSessionOp(id: number): Promise<void> {
  await db.pendingSessionOps.delete(id);
}
